import React, { useState, useEffect, useRef } from 'react';
import {
  ClockIcon as Clock
} from '@heroicons/react/24/outline';
import { BellIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

interface TimeAlert {
  type: 'time_alert' | 'urgent_time_alert' | 'extension_completed' | 'auto_extend_enabled' | 'auto_extend_disabled';
  data: {
    remaining_minutes?: number;
    session_id: string;
    message: string;
    actions?: string[];
    extension_minutes?: number;
    new_end_time?: string;
    duration_minutes?: number;
  };
}

interface ExtensionOption {
  duration_minutes: number;
  duration_label: string;
  price: number;
  price_per_minute: number;
  recommended: boolean;
}

interface SessionInfo {
  session_id: string;
  end_time: string;
  user_id: string;
}

interface TimeAlertDisplayProps {
  userId: string;
  sessionId?: string;
  onExtensionRequest?: (minutes: number, paymentMethod: string) => void;
  onAutoExtendToggle?: (enabled: boolean, duration: number) => void;
}

const TimeAlertDisplay: React.FC<TimeAlertDisplayProps> = ({
  userId,
  sessionId,
  onExtensionRequest,
  onAutoExtendToggle
}) => {
  const [alerts, setAlerts] = useState<TimeAlert[]>([]);
  const [extensionOptions, setExtensionOptions] = useState<ExtensionOption[]>([]);
  const [currentAlert, setCurrentAlert] = useState<TimeAlert | null>(null);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [autoExtendEnabled, setAutoExtendEnabled] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState<number>(60);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [remainingMinutes, setRemainingMinutes] = useState<number | null>(null);

  // 알림 표시 상태 추적 (중복 알림 방지)
  const alertedTimes = useRef<Set<number>>(new Set());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 세션 정보 로드
  useEffect(() => {
    if (sessionId) {
      loadSessionInfo();
      loadExtensionOptions();
    }
  }, [sessionId]);

  // 타이머 시작
  useEffect(() => {
    if (sessionInfo) {
      startTimer();
      return () => {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }
      };
    }
  }, [sessionInfo]);

  const loadSessionInfo = async () => {
    if (!sessionId) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/study-sessions/${sessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSessionInfo({
          session_id: sessionId,
          end_time: data.end_time,
          user_id: userId
        });
      }
    } catch (error) {
      console.error('Failed to load session info:', error);
    }
  };

  const startTimer = () => {
    // 초기 체크
    checkRemainingTime();

    // 30초마다 체크
    checkIntervalRef.current = setInterval(() => {
      checkRemainingTime();
    }, 30000); // 30초
  };

  const checkRemainingTime = () => {
    if (!sessionInfo) return;

    const endTime = new Date(sessionInfo.end_time).getTime();
    const now = Date.now();
    const remainingMs = endTime - now;
    const remainingMins = Math.floor(remainingMs / 1000 / 60);

    setRemainingMinutes(remainingMins);

    // 알림 조건 체크
    if (remainingMins <= 0 && !alertedTimes.current.has(0)) {
      showAlert('urgent_time_alert', 0, '⏰ 이용 시간이 종료되었습니다!', ['extend']);
      alertedTimes.current.add(0);
    } else if (remainingMins === 1 && !alertedTimes.current.has(1)) {
      showAlert('urgent_time_alert', 1, '⚠️ 1분 남았습니다! 시간을 연장하시겠습니까?', ['extend']);
      alertedTimes.current.add(1);
      playNotificationSound('urgent_time_alert');
    } else if (remainingMins === 5 && !alertedTimes.current.has(5)) {
      showAlert('urgent_time_alert', 5, '⏰ 5분 남았습니다! 시간을 연장하시겠습니까?', ['extend']);
      alertedTimes.current.add(5);
      playNotificationSound('urgent_time_alert');
    } else if (remainingMins === 10 && !alertedTimes.current.has(10)) {
      showAlert('time_alert', 10, 'ℹ️ 10분 남았습니다.', ['extend']);
      alertedTimes.current.add(10);
      playNotificationSound('time_alert');
    }
  };

  const showAlert = (type: TimeAlert['type'], minutes: number, message: string, actions?: string[]) => {
    const newAlert: TimeAlert = {
      type,
      data: {
        remaining_minutes: minutes,
        session_id: sessionId || '',
        message,
        actions
      }
    };

    setAlerts(prev => [newAlert, ...prev.slice(0, 4)]); // 최대 5개까지 유지
    setCurrentAlert(newAlert);

    // 긴급 알림인 경우 (5분 이하) 모달 자동 표시
    if (type === 'urgent_time_alert' && minutes <= 5) {
      setShowExtensionModal(true);
    }
  };

  const playNotificationSound = (alertType: string) => {
    try {
      const audio = new Audio();
      if (alertType === 'urgent_time_alert') {
        audio.src = '/sounds/urgent-alert.mp3'; // 긴급 알림음
      } else {
        audio.src = '/sounds/notification.mp3'; // 일반 알림음
      }
      audio.play().catch(e => console.log('Could not play notification sound:', e));
    } catch (error) {
    }
  };

  const loadExtensionOptions = async () => {
    if (!sessionId) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/alerts/time/extension/options?session_id=${sessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setExtensionOptions(data.extension_options.extension_options || []);
        }
      }
    } catch (error) {
      console.error('Failed to load extension options:', error);
    }
  };

  const handleExtensionRequest = async (minutes: number, paymentMethod: string) => {
    if (!sessionId) return;

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/alerts/time/extension/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          extension_minutes: minutes,
          payment_method: paymentMethod
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShowExtensionModal(false);
          setCurrentAlert(null);

          // 세션 정보 다시 로드 (새로운 종료 시간 반영)
          await loadSessionInfo();

          // 알림 상태 초기화 (연장 후 다시 알림 받을 수 있도록)
          alertedTimes.current.clear();

          // 성공 알림 표시
          setAlerts(prev => [{
            type: 'extension_completed',
            data: {
              session_id: sessionId,
              message: `✅ ${minutes}분 연장되었습니다!`,
              extension_minutes: minutes,
              new_end_time: data.extension_result.new_end_time
            }
          }, ...prev.slice(0, 4)]);

          onExtensionRequest?.(minutes, paymentMethod);
        } else {
          alert('연장 요청 처리 중 오류가 발생했습니다.');
        }
      }
    } catch (error) {
      console.error('Failed to request extension:', error);
      alert('연장 요청 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAutoExtendToggle = async () => {
    if (!sessionId) return;

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('authToken');

      if (autoExtendEnabled) {
        // 자동 연장 해제
        const response = await fetch(
          `${API_BASE_URL}/api/v1/alerts/time/auto-extend/disable?session_id=${sessionId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          setAutoExtendEnabled(false);
          onAutoExtendToggle?.(false, 0);
        }
      } else {
        // 자동 연장 설정
        const response = await fetch(`${API_BASE_URL}/api/v1/alerts/time/auto-extend/enable`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            session_id: sessionId,
            auto_extend_duration: 60,
            payment_method: selectedPaymentMethod
          })
        });

        if (response.ok) {
          setAutoExtendEnabled(true);
          onAutoExtendToggle?.(true, 60);
        }
      }
    } catch (error) {
      console.error('Failed to toggle auto extend:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const dismissAlert = (alert: TimeAlert) => {
    setAlerts(prev => prev.filter(a => a !== alert));
    if (currentAlert === alert) {
      setCurrentAlert(null);
    }
  };

  const getAlertIcon = (alertType: string) => {
    switch (alertType) {
      case 'urgent_time_alert':
        return <Clock className="h-5 w-5 text-red-500" />;
      case 'extension_completed':
        return <Clock className="h-5 w-5 text-green-500" />;
      case 'auto_extend_enabled':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'auto_extend_disabled':
        return <Clock className="h-5 w-5 text-gray-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getAlertBgColor = (alertType: string) => {
    switch (alertType) {
      case 'urgent_time_alert':
        return 'bg-red-50 border-red-200';
      case 'extension_completed':
        return 'bg-green-50 border-green-200';
      case 'auto_extend_enabled':
        return 'bg-blue-50 border-blue-200';
      case 'auto_extend_disabled':
        return 'bg-gray-50 border-gray-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const formatRemainingTime = (minutes: number | null) => {
    if (minutes === null) return '-';
    if (minutes <= 0) return '종료됨';

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}시간 ${mins}분`;
    }
    return `${mins}분`;
  };

  return (
    <div className="space-y-4">
      {/* 타이머 기반 알림 상태 표시 */}
      <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full mr-2 bg-green-500"></div>
          <span className="text-sm text-gray-600">
            타이머 알림 활성화
          </span>
          {remainingMinutes !== null && (
            <span className="ml-2 text-sm font-medium text-gray-900">
              (남은 시간: {formatRemainingTime(remainingMinutes)})
            </span>
          )}
        </div>

        {sessionId && (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleAutoExtendToggle}
              disabled={isProcessing}
              className={`flex items-center px-3 py-1 text-xs rounded-full transition-colors ${
                autoExtendEnabled
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}
            >
              <Clock className="h-3 w-3 mr-1" />
              자동연장 {autoExtendEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
        )}
      </div>

      {/* 알림 목록 */}
      <div className="space-y-2">
        {alerts.map((alert, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border ${getAlertBgColor(alert.type)} transition-all duration-300`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                {getAlertIcon(alert.type)}
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {alert.data.message}
                  </p>
                  {alert.data.remaining_minutes !== undefined && (
                    <p className="text-xs text-gray-600 mt-1">
                      세션 ID: {alert.data.session_id}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {alert.data.actions?.includes('extend') && (
                  <button
                    onClick={() => {
                      setCurrentAlert(alert);
                      setShowExtensionModal(true);
                    }}
                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                  >
                    연장
                  </button>
                )}

                <button
                  onClick={() => dismissAlert(alert)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 시간 연장 모달 */}
      {showExtensionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">시간 연장</h3>
              <button
                onClick={() => setShowExtensionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {currentAlert && currentAlert.data.remaining_minutes !== undefined && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  ⏰ 현재 남은 시간: <span className="font-bold">{currentAlert.data.remaining_minutes}분</span>
                </p>
              </div>
            )}

            {/* 연장 옵션 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                연장 시간 선택
              </label>
              <div className="grid grid-cols-2 gap-2">
                {extensionOptions.map((option) => (
                  <button
                    key={option.duration_minutes}
                    onClick={() => setSelectedExtension(option.duration_minutes)}
                    className={`p-3 text-left border rounded-lg transition-colors ${
                      selectedExtension === option.duration_minutes
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium">{option.duration_label}</div>
                    <div className="text-sm text-gray-600">
                      {option.price.toLocaleString()}원
                    </div>
                    {option.recommended && (
                      <div className="text-xs text-blue-600 font-medium">추천</div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* 결제 방법 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                결제 방법
              </label>
              <div className="space-y-2">
                {[
                  { id: 'card', label: '신용/체크카드' },
                  { id: 'kakao_pay', label: '카카오페이' },
                  { id: 'toss_pay', label: '토스페이' }
                ].map(({ id, label }) => (
                  <label key={id} className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={id}
                      checked={selectedPaymentMethod === id}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowExtensionModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() => handleExtensionRequest(selectedExtension, selectedPaymentMethod)}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isProcessing ? '처리중...' : `${selectedExtension}분 연장`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 알림이 없을 때 */}
      {alerts.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <BellIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">현재 활성 알림이 없습니다</p>
        </div>
      )}
    </div>
  );
};

export default TimeAlertDisplay;
